import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAraTheme } from '../contexts/AraThemeContext';
import { ArrowRight, Sun, Moon, Shield, Heart, UsersThree } from 'phosphor-react';

/*
 * AraLanding — independent entry point for Ara (the supporter's sanctuary).
 * Reachable at /ara (and linked from the AraVela landing with a secondary CTA).
 */
const AraLanding = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useAraTheme();
  const logo = theme === 'dark' ? '/ara-logo-dark.png' : '/ara-logo-light.png';

  return (
    <div className="ara-root min-h-screen" data-ara-theme={theme} data-testid="ara-landing">
      <header className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={logo} alt="Ara" className="w-10 h-10 rounded-full" />
          <span className="font-serif text-xl font-semibold ara-text">Ara</span>
        </div>
        <button
          onClick={toggleTheme}
          className="ara-chip inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm"
          data-testid="ara-theme-toggle"
          aria-label="Toggle theme"
        >
          {theme === 'dark' ? <Sun size={18} weight="duotone" /> : <Moon size={18} weight="duotone" />}
          <span className="hidden sm:inline">{theme === 'dark' ? 'Light' : 'Dark'} mode</span>
        </button>
      </header>

      <main className="max-w-3xl mx-auto px-6 pt-6 pb-16">
        <div className="flex justify-center mb-8">
          <img src={logo} alt="" className="w-40 h-40 sm:w-56 sm:h-56 rounded-full ara-logo-glow" />
        </div>

        <h1 className="text-center text-4xl sm:text-5xl font-serif font-semibold ara-text leading-tight mb-4" data-testid="ara-landing-title">
          A sanctuary for the ones who care
        </h1>
        <p className="text-center text-lg ara-text-muted max-w-2xl mx-auto mb-10 leading-relaxed">
          Ara is a private, AI-supported companion for people who support others —
          partners, family, friends, caregivers, and anyone navigating a difficult
          relationship. You are a whole person, too.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-16">
          <button
            onClick={() => navigate('/ara/start')}
            className="ara-cta-primary inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 font-medium w-full sm:w-auto"
            data-testid="ara-begin-btn"
          >
            Enter Ara
            <ArrowRight size={18} weight="bold" />
          </button>
          <button
            onClick={() => navigate('/')}
            className="ara-cta-ghost inline-flex items-center justify-center rounded-full px-6 py-4 text-sm w-full sm:w-auto"
            data-testid="ara-back-to-aravela"
          >
            Back to AraVela
          </button>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <div className="ara-card rounded-2xl p-5">
            <Heart size={22} weight="duotone" className="ara-accent mb-3" />
            <h3 className="font-medium ara-text mb-1">For your whole self</h3>
            <p className="text-sm ara-text-muted">Care, frustration, needs, boundaries — all held with compassion.</p>
          </div>
          <div className="ara-card rounded-2xl p-5">
            <UsersThree size={22} weight="duotone" className="ara-accent mb-3" />
            <h3 className="font-medium ara-text mb-1">Relational, not clinical</h3>
            <p className="text-sm ara-text-muted">Reflect on interactions and communication without diagnosing anyone.</p>
          </div>
          <div className="ara-card rounded-2xl p-5">
            <Shield size={22} weight="duotone" className="ara-accent mb-3" />
            <h3 className="font-medium ara-text mb-1">Private by design</h3>
            <p className="text-sm ara-text-muted">Encrypted on your device with your own passcode. Nothing stored on servers.</p>
          </div>
        </div>

        <p className="text-center text-xs ara-text-muted mt-12">
          Ara is a supportive companion, not therapy or a replacement for human
          connection.
        </p>
      </main>
    </div>
  );
};

export default AraLanding;
